import { computed, ref } from "vue";
import type { Ref } from "vue";

const clamp = (val: number, min = 0, max = 1): number =>
  Math.max(min, Math.min(max, val));

function useClamp(value: number, min: number, max: number): Ref<number> {
  const _value = ref(value);
  return computed<number>({
    get() {
      return clamp(_value.value, min, max);
    },
    set(value) {
      _value.value = clamp(value, min, max);
    },
  });
}

const range = (min: number, max: number): number[] =>
  Array.from({ length: max - min + 1 }, (_, i) => min + i);

const SEPARATOR = "dots";

export function usePagination({
  siblings = 2,
  boundaries = 1,
  total = 0,
  initialPage = 1,
}) {
  const page = useClamp(initialPage, 1, total);

  const paginationRange = computed<(number | "dots")[]>(() => {
    // Pages count is determined as siblings (left/right) + boundaries(left/right) + currentPage + 2*SEPARATOR
    const totalPageNumbers = siblings * 2 + boundaries * 2 + 3;

    /*
     * If the number of pages is less than the page numbers we want to show in our
     * paginationComponent, we return the range [1..total]
     */
    if (totalPageNumbers >= total) {
      return range(1, total);
    }

    const leftSiblingIndex = Math.max(page.value - siblings, boundaries);

    const rightSiblingIndex = Math.min(
      page.value + siblings,
      total - boundaries,
    );

    /*
     * We do not want to show dots if there is only one position left
     * after/before the left/right page count as that would lead to a change if our Pagination
     * component size which we do not want
     */
    // const shouldShowLeftDots = leftSiblingIndex > boundaries + 2;
    const iteratorLeft = siblings * 2 + boundaries <= page.value ? 1 : 0;

    const shouldShowLeftDots =
      leftSiblingIndex >= boundaries + 1 + iteratorLeft;

    // const shouldShowRightDots = rightSiblingIndex < total - (boundaries + 1);

    const shouldShowRightDots = rightSiblingIndex + 1 < total;

    if (!shouldShowLeftDots && shouldShowRightDots) {
      // const leftItemCount = siblings * 2 + boundaries + 2;

      const leftItemCount = siblings * 2 + boundaries;

      return [
        ...range(1, leftItemCount + iteratorLeft),
        SEPARATOR,
        ...range(total - (boundaries - 1), total),
      ];
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      // const rightItemCount = boundaries + 1 + 2 * siblings;
      const rightItemCount = boundaries + 2 * siblings;

      return [
        ...range(1, boundaries),
        SEPARATOR,
        ...range(total - rightItemCount, total),
      ];
    }

    return [
      ...range(1, boundaries),
      SEPARATOR,
      ...range(leftSiblingIndex, rightSiblingIndex),
      SEPARATOR,
      ...range(total - boundaries + 1, total),
    ];
  });

  return { currentPage: page, paginationRange };
}
